'use client';

import {type PlaceSummary} from '#/cms/schemas/place-summary.schema';
import Box from './Box';
import {type ComponentProps, useId, useMemo, useState} from 'react';
import classNames from 'classnames';
import {usePlaceStore} from '../store/placeStore';
import TextSearchBox from './TextSearchBox';
import Select from './Select';
import {type Radius, type RadiusOption, useRadiusOptions} from '#/utils/geo';
import {Heading3, SmallText} from './Text';
import {useUiStrings} from '#/features/store/uiStrings';
import RadioInput from '#/features/components/RadioInput';
import {PopoverTooltip} from './PopoverTooltip';

type Props = ComponentProps<'div'> & {
  availablePlaces: PlaceSummary['id'][];
  selectedPlace: PlaceSummary['id'] | null;
  selectedRadius: string | null;
  onSelectPlace: (placeId: PlaceSummary['id'] | null) => void | Promise<void>;
  onSelectRadius: (radius: RadiusOption) => void | Promise<void>;
  unMountInputs?: boolean;
  tooltipWithEmphasis?: string;
};

export const PlaceFilter = (props: Props) => {
  const {
    className,
    availablePlaces,
    selectedPlace,
    selectedRadius,
    onSelectPlace,
    onSelectRadius,
    unMountInputs = false,
    tooltipWithEmphasis,
  } = props;
  const {placeMap} = usePlaceStore();
  const radiusOptions = useRadiusOptions();
  const radioGroupName = useId();
  const [searchText, setSearchText] = useState('');

  const uiStrings = useUiStrings();
  const places = useMemo<PlaceSummary[]>(
    () =>
      availablePlaces
        .map(placeId => placeMap.get(placeId)!)
        .filter(place => {
          if (!place) return false;
          if (!!searchText && !place.title?.toLowerCase().includes(searchText.toLowerCase())) return false;
          return true;
        })
        .sort((a, b) => ((a['(ordering)'] ?? 0) > (b['(ordering)'] ?? 0) ? 1 : -1)),
    [availablePlaces, placeMap, searchText]
  );
  const radius = useMemo(
    () => (selectedRadius ? (radiusOptions[selectedRadius as Radius] ?? null) : null),
    [radiusOptions, selectedRadius]
  );

  return (
    <Box className={classNames('gap-y-2', className)}>
      <div className="flex flex-row items-center justify-between gap-x-2">
        <Heading3 className="text-lg">{uiStrings.general.locationTitle}</Heading3>
        {!!tooltipWithEmphasis && <PopoverTooltip tooltipWithEmphasis={tooltipWithEmphasis} />}
      </div>
      <Box className="gap-1 !rounded-[1.5rem] !px-3">
        <TextSearchBox
          placeholder="Search for an area"
          text={searchText}
          onTextChange={setSearchText}
          unMountInput={unMountInputs}
          id={'location-filter'}
        />
        <div className="mt-1 max-h-[6rem] overflow-y-auto scrollbar">
          <div className="ms-2 mt-1 flex flex-col gap-y-1">
            <div className="flex flex-row items-center gap-x-2" onClick={() => onSelectPlace(null)}>
              {unMountInputs && <p id={`all-${unMountInputs}`}>{uiStrings.general.locationAll}</p>}
              {!unMountInputs && (
                <>
                  <RadioInput
                    type="radio"
                    id={`all-${unMountInputs}`}
                    checked={selectedPlace === null}
                    name={radioGroupName}
                    readOnly
                  />
                  <label htmlFor={`all-${unMountInputs}`}>
                    <SmallText>{uiStrings.general.locationAll}</SmallText>
                  </label>
                </>
              )}
            </div>
            {places.map(place => (
              <div
                className="flex flex-row items-center gap-x-2"
                onClick={() => onSelectPlace(place.id)}
                key={place.id}
              >
                {unMountInputs && <p id={`${place.id}-${unMountInputs}`}>{place.title ?? ''}</p>}
                {!unMountInputs && (
                  <>
                    <RadioInput
                      type="radio"
                      id={place.id}
                      checked={place.id === selectedPlace}
                      name={radioGroupName}
                      readOnly
                    />
                    <label htmlFor={place.id}>
                      <SmallText>{place.title}</SmallText>
                    </label>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      </Box>
      {!!selectedPlace && (
        <div className="flex flex-col gap-y-1">
          <SmallText>{uiStrings.general.locationRadius}</SmallText>
          <Select value={radius} options={Object.values(radiusOptions)} onChange={onSelectRadius} />
        </div>
      )}
    </Box>
  );
};
